import axios from 'axios';
import mockAPI from '../mocks/mockServices';
import logger from '../utils/logger';

// 判断是否使用模拟API
const useMockAPI = false; // 设置为false表示使用真实API

// 创建axios实例
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
  timeout: 120000 // 120秒超时
});

// 创建一个全局的通知函数，避免循环依赖
let globalNotificationHandler = null;

// 设置全局通知处理器的函数
export const setGlobalNotificationHandler = (handler) => {
  globalNotificationHandler = handler;
};

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 从本地存储获取token
    const token = localStorage.getItem('token');

    // 如果有token则添加到请求头
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    // 直接返回响应数据
    return response.data;
  },
  (error) => {
    // 详细记录错误信息，方便调试
    //console.error('API请求错误:', error.config?.url, error.response?.status);

    if (error.response) {
      const statusCode = error.response.status;

      // 处理401未授权错误
      if (statusCode === 401) {
        //console.log('收到401未授权错误，错误详情:', error.response.data);

        // 如果是以下接口返回401，不重定向：
        // - 登录接口：正常的用户名密码错误
        // - 获取用户信息接口：认证检查发现token过期，应该由认证逻辑处理，不强制跳转
        // - 配置接口：语种和状态配置接口，应该在组件层面处理
        const noRedirectUrls = ['/auth/login', '/user/info', '/config/languages', '/config/statuses'];
        const shouldNotRedirect = noRedirectUrls.some(url => error.config.url.includes(url));

        if (!shouldNotRedirect) {
          // 清除token
          localStorage.removeItem('token');

          // 重定向到首页
          if (window.location.pathname !== '/landing') {
            window.location.href = '/landing';
          }
        }
      }
      // 处理403禁止访问错误
      else if (statusCode === 403) {
        //console.log('收到403禁止访问错误，错误详情:', error.response.data);

        const errorMessage = error.response.data?.message || '';

        // 如果是账号被禁用
        if (errorMessage.includes('账号已被禁用') || errorMessage.includes('账号被禁用')) {
          // 清除认证信息
          localStorage.removeItem('token');

          // 删除cookie中的token
          document.cookie = 'token=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';

          // 显示友好提示
          if (globalNotificationHandler) {
            globalNotificationHandler('您的账号已被管理员禁用，请联系客服处理。');
          }

          // 跳转到账号禁用页面
          if (window.location.pathname !== '/account-disabled') {
            window.location.href = '/account-disabled';
          }
        }
        // 其他403错误（如权限不足）
        else {
          //console.log('权限不足或其他403错误:', errorMessage);
        }
      }
      // 其他HTTP错误码不重定向，让各组件自行处理
      else {
        //console.log(`收到${statusCode}错误，错误详情:`, error.response.data);
      }
    } else {
      // 网络错误等非HTTP状态码错误
      //console.error('网络请求失败:', error.message);
    }

    return Promise.reject(error);
  }
);

// 身份认证相关API
export const authAPI = {
  // 登录
  login: async (username, password) => {
    if (useMockAPI) {
      return mockAPI.auth.login(username, password);
    }

    try {
      const response = await api.post('/auth/login', { username, password });

      // 检查响应格式，确保符合预期
      if (response.success === false && response.message) {
        throw new Error(response.message);
      }

      //console.log('登录API响应:', response);

      return response;
    } catch (error) {
      //console.error('登录API错误:', error);
      throw error;
    }
  },

  // 发送短信验证码
  sendSmsCode: async (phone, type = 'login') => {
    if (useMockAPI) {
      return mockAPI.auth.sendSmsCode(phone, type);
    }

    try {
      // 构建请求参数，当phone为null时不传递phone参数
      const requestData = { type };
      if (phone !== null) {
        requestData.phone = phone;
      }

      const response = await api.post('/auth/sms/send', requestData);

      //console.log('发送验证码API响应:', response);

      return response;
    } catch (error) {
      //console.error('发送验证码API错误:', error);
      throw error;
    }
  },

  // 手机号验证码登录
  smsLogin: async (phone, code) => {
    if (useMockAPI) {
      return mockAPI.auth.smsLogin(phone, code);
    }

    try {
      const response = await api.post('/auth/sms/login', { phone, code });

      // 检查响应格式，确保符合预期
      if (response.success === false && response.message) {
        throw new Error(response.message);
      }

      //console.log('手机号登录API响应:', response);

      return response;
    } catch (error) {
      //console.error('手机号登录API错误:', error);
      throw error;
    }
  },

  // 生成微信登录二维码
  generateWechatQRCode: async (type = 'login') => {
    if (useMockAPI) {
      return mockAPI.auth.generateWechatQRCode(type);
    }

    try {
      const response = await api.post('/auth/wechat/qrcode', { type });

      //console.log('生成微信二维码API响应:', response);

      return response;
    } catch (error) {
      //console.error('生成微信二维码API错误:', error);
      throw error;
    }
  },

  // 查询微信二维码状态
  getWechatQRCodeStatus: async (scene_str) => {
    if (useMockAPI) {
      return mockAPI.auth.getWechatQRCodeStatus(scene_str);
    }

    try {
      const response = await api.get(`/auth/wechat/qrcode/status/${scene_str}`);

      //console.log('查询微信二维码状态API响应:', response);

      return response;
    } catch (error) {
      //console.error('查询微信二维码状态API错误:', error);
      throw error;
    }
  },

  // 退出登录
  logout: () => {
    return useMockAPI
      ? mockAPI.auth.logout()
      : api.post('/auth/logout');
  },

  // 获取用户信息
  getUserInfo: () => {
    return useMockAPI
      ? mockAPI.auth.getUserInfo()
      : api.get('/user/info');
  },

  // 获取积分交易记录
  getPointsTransactions: async (params = {}) => {
    if (useMockAPI) {
      return mockAPI.auth.getPointsTransactions(params);
    }

    try {
      // 构建查询参数
      const queryParams = {};

      // 添加分页参数
      if (params.page) {
        queryParams.page = params.page;
      }
      if (params.limit) {
        queryParams.limit = params.limit;
      }

      // 添加类型筛选参数
      if (params.type && params.type !== 'all') {
        queryParams.type = params.type;
      }

      //console.log('获取积分交易记录API请求参数:', queryParams);

      const response = await api.get('/user/points/transactions', { params: queryParams });

      //console.log('获取积分交易记录API响应:', response);

      return response;
    } catch (error) {
      //console.error('获取积分交易记录API错误:', error);
      throw error;
    }
  },

  // 修改用户名
  updateUsername: async (username) => {
    if (useMockAPI) {
      return mockAPI.auth.updateUsername(username);
    }

    try {
      const response = await api.put('/user/username', { username });

      //console.log('修改用户名API响应:', response);

      return response;
    } catch (error) {
      //console.error('修改用户名API错误:', error);
      throw error;
    }
  },

  // 绑定手机号
  bindPhone: async (phone, code) => {
    if (useMockAPI) {
      return mockAPI.auth.bindPhone(phone, code);
    }

    try {
      const response = await api.post('/auth/bind/phone', { phone, code });

      //console.log('绑定手机号API响应:', response);

      return response;
    } catch (error) {
      //console.error('绑定手机号API错误:', error);
      throw error;
    }
  },

  // 验证当前手机号（修改手机号第一步）
  verifyCurrentPhone: async (code) => {
    if (useMockAPI) {
      return mockAPI.auth.verifyCurrentPhone(code);
    }

    try {
      const response = await api.post('/auth/phone/change/verify-current', { code });

      //console.log('验证当前手机号API响应:', response);

      return response;
    } catch (error) {
      //console.error('验证当前手机号API错误:', error);
      throw error;
    }
  },

  // 验证新手机号并完成修改（修改手机号第二步）
  verifyNewPhoneAndComplete: async (phone, code, currentVerificationId) => {
    if (useMockAPI) {
      return mockAPI.auth.verifyNewPhoneAndComplete(phone, code, currentVerificationId);
    }

    try {
      const response = await api.post('/auth/phone/change/verify-new', {
        phone,
        code,
        currentVerificationId
      });

      //console.log('验证新手机号并完成修改API响应:', response);

      return response;
    } catch (error) {
      //console.error('验证新手机号并完成修改API错误:', error);
      throw error;
    }
  },

  // 绑定微信账号
  bindWechat: async (scene_str) => {
    if (useMockAPI) {
      return mockAPI.auth.bindWechat(scene_str);
    }

    try {
      const response = await api.post('/auth/bind/wechat', { scene_str });

      //console.log('绑定微信API响应:', response);

      return response;
    } catch (error) {
      //console.error('绑定微信API错误:', error);
      throw error;
    }
  },

  // 解除微信绑定
  unbindWechat: async () => {
    if (useMockAPI) {
      return mockAPI.auth.unbindWechat();
    }

    try {
      const response = await api.post('/auth/unbind/wechat', {});

      //console.log('解除微信绑定API响应:', response);

      return response;
    } catch (error) {
      //console.error('解除微信绑定API错误:', error);
      throw error;
    }
  }
};

// 任务相关API
export const tasksAPI = {
  // 获取任务列表
  getTasks: (params) => {
    return useMockAPI
      ? mockAPI.tasks.getTasks(params)
      : api.get('/tasks', { params });
  },

  // 上传文件创建任务
  uploadFiles: (files, language, certification_type = 'none', is_urgent = false) => {
    logger.debug('[API] 开始上传文件创建任务');
    logger.debug('[API] 上传参数:', {
      '翻译语种': language,
      '认证类型': certification_type,
      '是否加急': is_urgent,
      '文件数量': files.length,
      useMockAPI
    });

    if (useMockAPI) {
      logger.debug('[API] 使用模拟API');
      return mockAPI.tasks.uploadFiles(files, language, certification_type, is_urgent);
    }

    logger.debug('[API] 构建FormData');
    const formData = new FormData();

    // 添加文件
    files.forEach((file, index) => {
      // 创建一个带有正确编码文件名的新File对象
      const encodedFile = new File(
        [file],
        encodeURIComponent(file.name),
        { type: file.type }
      );

      logger.debug(`[API] 处理文件 ${index + 1}/${files.length}:`, {
        originalName: file.name,
        encodedName: encodedFile.name,
        type: file.type,
        size: file.size
      });

      // 使用编码后的文件对象
      formData.append('files', encodedFile);
    });

    // 添加语言参数
    formData.append('language', language);
    logger.debug('[API] 添加语言参数:', language);

    // 添加认证类型参数
    formData.append('certification_type', certification_type);
    logger.debug('[API] 添加认证类型参数:', certification_type);

    // 添加加急服务参数
    formData.append('is_urgent', is_urgent);
    logger.debug('[API] 添加加急服务参数:', is_urgent);

    logger.debug('[API] 发送POST请求到 /tasks/upload');

    // 使用try-catch确保错误能够被正确传递
    try {
      const requestPromise = api.post('/tasks/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // 直接返回原始的 Promise，让上层处理所有错误
      // 不在这里添加额外的 .then() 和 .catch() 处理器
      // 避免在 API 层面重新抛出错误，让 tasksActions 完全控制错误处理
      return requestPromise;
    } catch (error) {
      logger.error('[API] 文件上传请求创建失败:', error);
      throw error; // 确保错误被抛出
    }
  },

  // 获取任务详情
  getTaskDetail: (taskId) => {
    return useMockAPI
      ? mockAPI.tasks.getTaskDetail(taskId)
      : api.get(`/tasks/${taskId}`);
  },

  // 取消任务
  cancelTask: (taskId) => {
    return useMockAPI
      ? mockAPI.tasks.cancelTask(taskId)
      : api.post(`/tasks/${taskId}/cancel`);
  },

  // 下载任务结果（返回完整URL而非直接请求）
  getDownloadUrl: (taskId) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    return `${baseUrl}/tasks/${taskId}/download`;
  },

  // 删除任务文件
  deleteTaskFiles: (taskId) => {
    return useMockAPI
      ? mockAPI.tasks.deleteTaskFiles(taskId)
      : api.delete(`/tasks/${taskId}/files`);
  }
};

// 系统配置相关API
export const configAPI = {
  // 获取支持的语种
  getLanguages: () => {
    return useMockAPI
      ? mockAPI.config.getLanguages()
      : api.get('/config/languages');
  },

  // 获取任务状态列表
  getStatuses: () => {
    return useMockAPI
      ? mockAPI.config.getStatuses()
      : api.get('/config/statuses');
  }
};

// 订单相关API
export const orderAPI = {
  // 获取公开SKU列表（不需要登录）
  getSKUList: async (params = {}) => {
    if (useMockAPI) {
      // 模拟公开SKU列表
      return Promise.resolve({
        success: true,
        data: {
          skus: [
            {
              _id: 'mock-sku-1',
              sku_id: 'SKU-BASIC-001',
              sku_name: '基础套餐',
              sku_price: 29.9,
              sku_original_points: 100,
              sku_bonus_points: 20,
              sku_total_points: 120,
              description: '适合个人用户的基础翻译套餐',
              features: ['100原始点数', '20赠送点数', '永不过期'],
              popular: false,
              averagePagePrice: 0.25,
              discount: 20
            }
          ],
          count: 1
        },
        message: '获取SKU列表成功'
      });
    }

    try {
      const response = await api.get('/order/skus', { params });

      //console.log('获取公开SKU列表API响应:', response);

      return response;
    } catch (error) {
      //console.error('获取公开SKU列表API错误:', error);
      throw error;
    }
  },

  // 获取用户可用套餐列表
  getUserAvailableSkus: async (userId, params = {}) => {
    if (useMockAPI) {
      // 如果需要可以在mockAPI中添加对应方法
      return Promise.resolve({
        success: true,
        data: { skus: [], count: 0 },
        message: '获取用户可用SKU列表成功'
      });
    }

    try {
      const response = await api.get(`/order/skus/user/${userId}`, { params });

      //console.log('获取用户可用套餐API响应:', response);

      return response;
    } catch (error) {
      //console.error('获取用户可用套餐API错误:', error);
      throw error;
    }
  },

  // 获取原子商品列表
  getAtomicProducts: async (params = {}) => {
    if (useMockAPI) {
      // 模拟原子商品列表
      return Promise.resolve({
        success: true,
        data: {
          products: [
            {
              product_id: "PRODUCT-4C652B9A",
              product_name: "AI翻译",
              product_description: "大模型翻译服务，全自动、高质量、快速翻译",
              billing_type: "per_page",
              points_per_page: 2,
              points_per_time: 0,
              estimated_duration: 10,
              duration_unit: "minutes",
              category: "translation",
              tags: ["标准翻译", "签证文档", "快速交付"],
              billing_display: "按页计费：2点数/页",
              duration_display: "10分钟",
              created_at: "2025-06-28T03:20:03.960Z"
            },
            {
              product_id: "PRODUCT-719F602F",
              product_name: "专业认证服务",
              product_description: "官方人工审核和认证翻译服务，适用于法律、移民等正式文件",
              billing_type: "per_page",
              points_per_page: 8,
              points_per_time: 0,
              estimated_duration: 3,
              duration_unit: "hours",
              category: "translation",
              tags: ["官方认证", "人工审核", "翻译盖章", "质量保障"],
              billing_display: "按页计费：8点数/页",
              duration_display: "3小时",
              created_at: "2025-06-28T03:20:03.960Z"
            },
            {
              product_id: "PRODUCT-E6EA966F",
              product_name: "NAATI认证服务",
              product_description: "官方人工审核和澳大利亚NAATI认证翻译服务",
              billing_type: "per_page",
              points_per_page: 10,
              points_per_time: 0,
              estimated_duration: 1,
              duration_unit: "days",
              category: "translation",
              tags: ["NAATI盖章认证", "人工审核", "澳洲专用", "质量保障"],
              billing_display: "按页计费：10点数/页",
              duration_display: "1天",
              created_at: "2025-06-28T03:20:03.960Z"
            }
          ],
          count: 3
        },
        message: '获取可用原子商品列表成功'
      });
    }

    try {
      const response = await api.get('/order/products', { params });

      //console.log('获取原子商品列表API响应:', response);

      return response;
    } catch (error) {
      //console.error('获取原子商品列表API错误:', error);
      throw error;
    }
  },

  // 创建支付订单
  createPaymentOrder: async (skuId, paymentMethod = 'wechat') => {
    if (useMockAPI) {
      // 模拟创建支付订单
      return Promise.resolve({
        success: true,
        data: {
          order_id: `ORDER-${Date.now()}-MOCK`,
          qr_code_url: 'weixin://wxpay/bizpayurl?pr=mock123def',
          payment_amount: 99.99,
          sku_info: {
            sku_name: '测试套餐',
            sku_total_points: 250
          },
          expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
          expires_in: 1800
        },
        message: '订单创建成功'
      });
    }

    try {
      const response = await api.post('/order/payment/create', {
        sku_id: skuId,
        payment_method: paymentMethod
      });

      //console.log('创建支付订单API响应:', response);

      return response;
    } catch (error) {
      //console.error('创建支付订单API错误:', error);
      throw error;
    }
  },

  // 查询支付状态
  getPaymentStatus: async (orderId) => {
    if (useMockAPI) {
      // 模拟支付状态查询
      const statuses = ['pending', 'paying', 'success'];
      const randomStatus = statuses[Math.floor(Math.random() * statuses.length)];

      return Promise.resolve({
        success: true,
        data: {
          order_id: orderId,
          order_status: randomStatus === 'success' ? 'paid' : 'pending',
          payment_status: randomStatus,
          paid_at: randomStatus === 'success' ? new Date().toISOString() : null,
          points_charged: randomStatus === 'success' ? 250 : 0,
          remaining_time: randomStatus === 'success' ? 0 : 25
        },
        message: '查询支付状态成功'
      });
    }

    try {
      const response = await api.get(`/order/payment/status/${orderId}`);

      //console.log('查询支付状态API响应:', response);

      return response;
    } catch (error) {
      //console.error('查询支付状态API错误:', error);
      throw error;
    }
  },

  // 取消支付订单
  cancelPaymentOrder: async (orderId, reason = '用户主动取消') => {
    if (useMockAPI) {
      // 模拟取消支付订单
      return Promise.resolve({
        success: true,
        data: {
          order_id: orderId,
          order_status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          cancel_reason: reason
        },
        message: '订单取消成功'
      });
    }

    try {
      const response = await api.post(`/order/payment/cancel/${orderId}`, {
        reason
      });

      //console.log('取消支付订单API响应:', response);

      return response;
    } catch (error) {
      //console.error('取消支付订单API错误:', error);
      throw error;
    }
  }
};

export default api;